<template>
  <b-modal
    id="extra-branches-modal"
    v-model="extraBranchesModal"
    dialog-class="extra-branches-modal"
    body-class="pb-2"
    size="md"
    centered
    hide-footer
    :title="modalTitle"
    lazy
  >
    <error-alert :error="quoteError" />

    <div class="font-weight-semi-bold text-secondary mb-1">
      {{ $t('branch.available-branches-count') }}: {{ availableBranchesLabel }}
    </div>
    <div class="font-weight-semi-bold text-secondary mb-2">
      {{ $t('branch.used-branches') }}: {{ usedBranchesLabel }}
    </div>
    <div class="text-danger font-weight-semi-bold mb-1">
      {{ limitReachedLabel }}
    </div>
    <div class="text-secondary mb-2">
      {{ capacityHintLabel }}
    </div>
    <div class="font-weight-bold mb-1 text-secondary">
      {{ $t('branch.how-many-extra-branches') }}
    </div>
    <div class="d-flex align-items-center mb-1">
      <span class="mr-1">{{ $t('branch.quantity') }}</span>
      <b-form-spinbutton
        v-model="quantity"
        min="1"
        :max="9999"
        class="extra-branches-qty"
      />
    </div>
    <div
      v-if="showCycleSwitch"
      class="d-flex align-items-center mb-1"
    >
      <b-form-radio-group
        v-model="cycle"
        :options="cycleOptions"
        buttons
        button-variant="outline-secondary"
        size="sm"
      />
    </div>
    <div
      v-if="unitPrice || unitPrice === 0"
      class="text-secondary mb-1"
    >
      {{ $t('branch.price-per-branch') }}: {{ $currencyFormat(unitPrice, 0) }}
    </div>
    <div class="font-weight-semi-bold mb-2">
      {{ $t('branch.total-price') }}: {{ $currencyFormat(totalPrice, 0) }}
    </div>
    <div class="d-flex justify-content-end mt-2">
      <b-button
        variant="outline-secondary"
        class="mr-1"
        @click="closeModal"
      >
        {{ $t('branch.close') }}
      </b-button>
      <b-button
        variant="dark"
        size="lg"
        :disabled="!canSubmit"
        @click="handleDone"
      >
        <b-spinner
          v-if="quoteLoading"
          small
        />
        <span v-else>
          {{ $t('branch.done') }}
        </span>
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BFormSpinbutton, BFormRadioGroup, BSpinner,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import AddonCycleEnum from '@/models/subscription/AddonCycleEnum'
import { ADDON_SLUGS } from '@/constants/features'
import ErrorAlert from '@/@okmenu/components/alerts/ErrorAlert'

export default {
  name: 'ExtraBranchesModal',
  components: {
    ErrorAlert,
    BModal,
    BButton,
    BFormSpinbutton,
    BFormRadioGroup,
    BSpinner,
  },
  data() {
    return {
      quantity: 1,
      cycle: 'monthly',
      quoteTimer: null,
      syncingQuote: false,
    }
  },
  computed: {
    ...mapGetters({
      quote: 'branches/extraBranchesQuote',
      quoteError: 'branches/extraBranchesQuoteError',
      quoteLoading: 'branches/extraBranchesQuoteLoading',
      isMonthlyPeriod: 'subscription/isMonthlyPeriod',
    }),
    extraBranchesModal: {
      get() { return this.$store.getters['branches/extraBranchesModal'] },
      set(val) { this.$store.commit('branches/SET_EXTRA_BRANCHES_MODAL', val) },
    },
    modalTitle() {
      return this.$t('branch.add-extra-branches')
    },
    cycleMatchesQuote() {
      const billingCycle = Number(this.quote?.billing_cycle)
      if (billingCycle === AddonCycleEnum.YEARLY) return this.cycle === 'yearly'
      if (billingCycle === AddonCycleEnum.MONTHLY) return this.cycle === 'monthly'
      return this.quote?.price_per_branch != null
    },
    availableBranchesLabel() {
      if (this.quote?.unlimited) return '∞'
      if (this.quote?.available_branches == null) return '—'
      return this.quote.available_branches
    },
    usedBranchesLabel() {
      if (this.quote?.used_branches == null) return '—'
      return this.quote.used_branches
    },
    limitReachedLabel() {
      return this.quoteError?.response?.data?.message || this.$t('branch.branch-limit-reached')
    },
    capacityHintLabel() {
      return this.quoteError?.response?.data?.args?.hint || this.$t('branch.add-more-branch-capacity')
    },
    unitPrice() {
      if (!this.cycleMatchesQuote || this.quote?.price_per_branch == null) return null
      return this.quote.price_per_branch
    },
    totalPrice() {
      if (this.unitPrice == null) return 0
      return this.unitPrice * Number(this.quantity)
    },
    showCycleSwitch() {
      return this.quote?.monthly_price != null && this.quote?.yearly_price != null
    },
    cycleOptions() {
      return [
        { text: this.$t('payment.monthly'), value: 'monthly' },
        { text: this.$t('payment.annually'), value: 'yearly' },
      ]
    },
    canSubmit() {
      return !this.quoteLoading && this.unitPrice != null && !!this.quote?.addon_id && this.quantity >= 1
    },
  },
  watch: {
    extraBranchesModal(visible) {
      if (!visible) return
      this.syncingQuote = true
      this.quantity = 1
      this.cycle = this.isMonthlyPeriod ? 'monthly' : 'yearly'
      this.$nextTick(() => {
        this.syncingQuote = false
        const priceKnown = this.quote?.price_per_branch != null && this.cycleMatchesQuote
        if (!priceKnown) this.refreshQuote()
      })
    },
    quantity(value) {
      const qty = Math.max(parseInt(value, 10) || 1, 1)
      if (qty !== value) this.quantity = qty
    },
    cycle() {
      if (!this.syncingQuote && this.extraBranchesModal) this.scheduleQuote()
    },
  },
  beforeDestroy() {
    clearTimeout(this.quoteTimer)
  },
  methods: {
    closeModal() {
      this.extraBranchesModal = false
    },
    scheduleQuote() {
      clearTimeout(this.quoteTimer)
      this.quoteTimer = setTimeout(() => this.refreshQuote(), 250)
    },
    refreshQuote() {
      return this.$store.dispatch('branches/FETCH_EXTRA_BRANCHES_QUOTE', {
        quantity: 1,
        cycle: this.cycle,
      }).catch(error => {
        if (error?.response?.status === 422) {
          this.syncingQuote = true
          this.quantity = 1
          this.$nextTick(() => { this.syncingQuote = false })
        }
      })
    },
    handleDone() {
      if (!this.canSubmit) return
      const quoteCycle = this.cycle === 'yearly' ? 'yearly' : 'monthly'
      this.$store.dispatch('purchase/INIT_PURCHASE', {
        addonSlug: ADDON_SLUGS.EXTRA_BRANCHES,
        addonLimit: this.quantity,
        cycle: quoteCycle,
        source: 'extra-branches',
      })
      this.$store.commit('branches/SET_OPEN_ADD_BRANCH_AFTER_PURCHASE', true)
      this.extraBranchesModal = false
      this.$router.push({
        name: 'billing:plans',
        query: {
          addons: ADDON_SLUGS.EXTRA_BRANCHES,
          limit: String(this.quantity),
          cycle: quoteCycle,
          redirect_to: this.$route.path,
        },
      }).catch(() => {})
    },
  },
}
</script>

<style lang="scss">
.extra-branches-modal {
  .extra-branches-qty {
    max-width: 120px;
    text-align: center !important;
  }
}
</style>
